import React, { useState } from 'react';
import { api } from '../../lib/api';
import { Sparkles, Loader2, User, TrendingUp, Award, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 60) return 'text-tp-accent bg-tp-accent/10 border-tp-accent/20';
  if (score >= 40) return 'text-orange-500 bg-orange-50 border-orange-200';
  return 'text-gray-400 bg-gray-50 border-gray-200';
};

const MissionSmartMatchesPanel = ({ missionId, onAddToShortlist }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runMatch = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/missions/${missionId}/smart-matches?limit=10`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
        if ((result.candidates || []).length === 0) {
          toast.info('Aucun candidat ne matche cette mission pour l\'instant');
        } else {
          toast.success(`${result.candidates.length} candidat(s) trouvé(s)`);
        }
      } else {
        toast.error('Erreur lors du matching');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6" data-testid="smart-matches-panel">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-tp-accent" />
          <h3 className="text-sm font-semibold text-tp-text">Smart matching</h3>
          {data && (
            <span className="text-xs text-gray-400">({data.total_matches} candidat{data.total_matches > 1 ? 's' : ''} trouvé{data.total_matches > 1 ? 's' : ''})</span>
          )}
        </div>
        <button
          onClick={runMatch}
          disabled={loading}
          className="text-xs px-3 py-1.5 bg-tp-accent hover:bg-tp-accent/90 text-white rounded-md flex items-center gap-1.5 transition-colors disabled:opacity-60"
          data-testid="run-smart-match-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {loading ? 'Recherche...' : 'Trouver des candidats'}
        </button>
      </div>

      {data?.detected_category_labels?.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3 text-xs">
          <span className="text-gray-500">Catégories détectées :</span>
          {data.detected_category_labels.map((lbl) => (
            <span key={lbl} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-medium">
              {lbl}
            </span>
          ))}
        </div>
      )}

      {!data ? (
        <p className="text-xs text-gray-400 italic">
          Cliquez sur "Trouver des candidats" pour obtenir les meilleurs profils basés sur la description de la mission.
        </p>
      ) : data.candidates?.length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          Aucun candidat ne matche les catégories de cette mission actuellement.
        </p>
      ) : (
        <div className="space-y-2" data-testid="smart-matches-list">
          {data.candidates.map((c) => (
            <div
              key={c.candidate_id}
              className="flex items-center gap-3 p-3 rounded-md border border-gray-100 hover:bg-gray-50 transition-colors"
              data-testid={`smart-match-${c.candidate_id}`}
            >
              <div className={`h-9 w-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${getScoreColor(c.match_score)}`}>
                {c.match_score}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm text-tp-text truncate">{c.name}</p>
                  {c.conversion_score >= 70 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-md font-semibold">
                      <TrendingUp className="h-2.5 w-2.5" /> {c.conversion_score}
                    </span>
                  )}
                  {c.qualification_score >= 70 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-md font-semibold">
                      <Award className="h-2.5 w-2.5" /> {c.qualification_score}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{c.main_profession || '—'}</p>
                {c.match_reasons?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {c.match_reasons.slice(0, 2).map((r, i) => (
                      <li key={`${c.candidate_id}-reason-${i}`} className="text-[11px] text-gray-600">
                        • {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {onAddToShortlist && (
                <button
                  onClick={() => onAddToShortlist(c.candidate_id)}
                  className="text-xs px-2 py-1 border border-gray-200 hover:border-tp-accent hover:text-tp-accent rounded-md flex items-center gap-1 shrink-0"
                  data-testid={`add-to-shortlist-${c.candidate_id}`}
                >
                  Ajouter <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MissionSmartMatchesPanel;
